import { useMemo } from 'react'

import { useAgentAttendanceShortQuery } from 'modules/Monitoring/api/analytics/queries'
import { GlobalFilterState } from 'modules/Monitoring/GlobalFilters/GlobalFilters'
import { useChannel } from 'modules/Monitoring/hooks/useChannel'
import { ResourcesSideParams, SpecialisationHealth } from 'modules/Monitoring/types'
import { UrlStateStorageKeys } from 'modules/Monitoring/url-state-storage/consts'
import { useHashedSearchParams } from 'modules/Monitoring/url-state-storage/useHashedSearchParams'
import { getStartOfDay } from 'modules/Monitoring/utils/time/getStartOfDay'
import { getUtcDate } from 'modules/Monitoring/utils/time/getUtcDate'

import { useQueueHealthAgentsParamsMap } from './useQueueHealthAgentsParamsMap'

export const useAgents = (
  queueHealthSpecialisations?: SpecialisationHealth[],
  resourcesSideParams?: ResourcesSideParams,
) => {
  const { channel, regionedChannel } = useChannel()
  const queueHealthAgentsParamsMap = useQueueHealthAgentsParamsMap(
    queueHealthSpecialisations,
    resourcesSideParams?.filterType,
  )

  const { state } = useHashedSearchParams<GlobalFilterState>({
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
    contextKey: regionedChannel,
  })

  const queueId = state.queues ?? []
  const specialisationId = state.specialisations ?? []
  const departmentId = state.departments ?? []
  const areaId = state.areas ?? []

  const startOfDayUtc = getUtcDate(getStartOfDay(new Date(), true)).getTime()

  const agentIds = useMemo(() => {
    const resourcesParamsQueueId = resourcesSideParams?.queue?.id
    const resourcesParamsSpecialisationId = resourcesSideParams?.specialisation?.id

    if (resourcesParamsQueueId) {
      return queueHealthAgentsParamsMap.get(resourcesParamsQueueId)
    }
    if (resourcesParamsSpecialisationId) {
      return queueHealthAgentsParamsMap.get(resourcesParamsSpecialisationId)
    }

    return undefined
  }, [resourcesSideParams, queueHealthAgentsParamsMap])

  const agentsAttendanceShortParams = {
    from: startOfDayUtc,
    to: startOfDayUtc,
    departmentId,
    areaId,
    specialisationId,
    queueId,
    agentId: agentIds ?? [],
    channel: [channel],
  }

  const { data: agents = [], isPending } = useAgentAttendanceShortQuery(
    agentsAttendanceShortParams,
    {
      enabled: Boolean(resourcesSideParams) && Boolean(agentIds?.length),
    },
  )

  return agentIds?.length
    ? {
        agents,
        agentIds,
        isPending,
      }
    : {
        agents: [],
        agentIds: [],
        isPending: false,
      }
}
